'use client'

import { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import Link from 'next/link'

type CategorySuggestion = {
  label: string
  href: string
}

export function SearchBar() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [categorySuggestion, setCategorySuggestion] =
    useState<CategorySuggestion | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  /* ------------------------------
     Cerrar resultados al hacer click afuera
  ------------------------------ */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClick)

    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  /* ------------------------------
     Fetch de búsqueda (con debounce)
  ------------------------------ */
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (query.trim().length < 2) {
        setResults([])
        setCategorySuggestion(null)
        setShowResults(false)
        return
      }

      try {
        const res = await fetch(
          '/api/search/simple?q=' + encodeURIComponent(query)
        )

        const data = await res.json()

        const nextResults = data.results || []
        const nextCategorySuggestion = data.categorySuggestion || null

        setResults(nextResults)
        setCategorySuggestion(nextCategorySuggestion)
        setShowResults(true)

        window.gtag?.('event', 'view_search_results', {
          search_term: query,

          results_count: nextResults.length,
        })
      } catch (error) {
        console.error('Error al buscar:', error)

        setResults([])
        setCategorySuggestion(null)
        setShowResults(true)
      }
    }, 250)

    return () => clearTimeout(timeout)
  }, [query])

  /* ------------------------------
     Limpiar búsqueda
  ------------------------------ */
  const clearSearch = () => {
    setQuery('')
    setResults([])
    setCategorySuggestion(null)
    setShowResults(false)
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef} className='relative w-full max-w-2xl mx-auto'>
      {/* ------------------ INPUT ------------------ */}
      <div
        className={`
          flex items-center gap-3
          bg-white
          border-2 rounded-full
          px-5 py-2.5
          transition-all duration-200

          ${
            isFocused
              ? 'border-[#00D084] shadow-[0_4px_16px_rgba(0,208,132,0.15)]'
              : 'border-[#E3E0D7] shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:border-[#D0CCC0]'
          }
        `}
      >
        <Search
          className={`w-5 h-5 transition-colors duration-200 ${
            isFocused ? 'text-[#00D084]' : 'text-[#9E9578]'
          }`}
        />

        <input
          ref={inputRef}
          type='text'
          placeholder='¿Qué estás buscando?'
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => {
            setIsFocused(true)

            if (
              query.trim().length >= 2 &&
              (results.length > 0 || categorySuggestion)
            ) {
              setShowResults(true)
            }
          }}
          onBlur={() => setTimeout(() => setIsFocused(false), 120)}
          className='
            flex-1 bg-transparent outline-none
            text-[#453F2F]
            placeholder-[#B7B0A0]
            text-[15px] font-medium
          '
        />

        {query && (
          <button
            type='button'
            onClick={clearSearch}
            className='
              p-1 rounded-full
              text-[#9E9578] hover:text-[#453F2F]
              hover:bg-[#F5F3EC]
              transition-all duration-200
            '
            aria-label='Limpiar búsqueda'
          >
            <X className='w-4 h-4' />
          </button>
        )}
      </div>

      {/* ------------------ RESULTADOS ------------------ */}
      {showResults && (categorySuggestion || results.length > 0) && (
        <div
          className='
            absolute w-full mt-3
            bg-white
            border-2 border-[#E3E0D7]
            rounded-2xl
            shadow-[0_8px_24px_rgba(0,0,0,0.12)]
            overflow-hidden z-50
            py-2
          '
        >
          {/* SUGERENCIA POR EDAD */}
          {categorySuggestion && (
            <Link
              href={categorySuggestion.href}
              className='
                block px-5 py-4
                bg-[#F0FBEF]
                text-[#453F2F]
                hover:text-[#00D084]
                transition-colors duration-150
              '
              onClick={() => {
                setShowResults(false)
              }}
            >
              <div className='mb-1 text-[11px] uppercase tracking-[0.14em] text-[#9E9578]'>
                Explorar por edad
              </div>

              <div className='flex items-center justify-between gap-3 text-[15px] font-medium'>
                <span>{categorySuggestion.label}</span>
                <span aria-hidden='true' className='text-[20px]'>
                  →
                </span>
              </div>
            </Link>
          )}

          {/* PRODUCTOS */}
          {results.map((product, index) => (
            <Link
              key={product.id}
              href={'/productos/' + product.slug}
              className={`
                block px-5 py-3
                text-[#453F2F] font-medium
                hover:bg-[#F0FBEF] hover:text-[#00D084]
                transition-all duration-150
                text-[15px]
                ${
                  index !== 0 || categorySuggestion
                    ? 'border-t border-[#F5F3EC]'
                    : ''
                }
              `}
              onClick={() => {
                setShowResults(false)

                window.gtag?.('event', 'select_search_result', {
                  search_term: query,
                  product_name: product.name,
                  product_slug: product.slug,
                })
              }}
            >
              <div className='flex items-center gap-2'>
                <Search className='w-4 h-4 opacity-40' />
                {product.name}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* ------------------ VACÍO ------------------ */}
      {showResults &&
        query.trim().length >= 2 &&
        results.length === 0 &&
        !categorySuggestion && (
          <div
            className='
              absolute w-full mt-3
              bg-white
              border-2 border-[#E3E0D7]
              rounded-2xl
              shadow-[0_8px_24px_rgba(0,0,0,0.12)]
              overflow-hidden z-50
              py-6 px-5
              text-center
            '
          >
            <p className='text-[#9E9578] text-[15px]'>
              No encontramos resultados para “{query}”
            </p>
          </div>
        )}
    </div>
  )
}
